import React from 'react';
import './StoryInfo.css';

interface StoryInfoProps {
  storyType: string;
  drawingType: string;
  drawingStyle: string;
  storyTone: string;
  canvasSize: string;
  backgroundColor?: string;
  customDrawingStyles?: string[];
  onUpdate: (field: 'storyType' | 'drawingType' | 'drawingStyle' | 'storyTone' | 'canvasSize' | 'backgroundColor', value: string) => void;
  onOpenStyleModal?: () => void;
  onOpenFontModal?: () => void;
  onOpenDesignModal?: () => void;
  showGrid?: boolean;
  onToggleGrid?: () => void;
  showPreviousPageOverlay?: boolean;
  onTogglePreviousPageOverlay?: () => void;
  previousPageOpacity?: number;
  onPreviousPageOpacityChange?: (opacity: number) => void;
  hasPreviousPage?: boolean;
}

const CANVAS_SIZES = [
  // Paperback Trim Sizes
  { value: '5x8', label: '5" × 8" (Paperback)', width: 500, height: 800 },
  { value: '5.06x7.81', label: '5.06" × 7.81" (Paperback)', width: 506, height: 781 },
  { value: '5.25x8', label: '5.25" × 8" (Paperback)', width: 525, height: 800 },
  { value: '5.5x8.5', label: '5.5" × 8.5" (Paperback)', width: 550, height: 850 },
  { value: '6x9', label: '6" × 9" (Paperback)', width: 600, height: 900 },
  
  // Large Paperback Trim Sizes
  { value: '6.14x9.21', label: '6.14" × 9.21" (Paperback)', width: 614, height: 921 },
  { value: '6.69x9.61', label: '6.69" × 9.61" (Paperback)', width: 669, height: 961 },
  { value: '7x10', label: '7" × 10" (Paperback)', width: 700, height: 1000 },
  { value: '7.44x9.69', label: '7.44" × 9.69" (Paperback)', width: 744, height: 969 },
  { value: '7.5x9.25', label: '7.5" × 9.25" (Paperback)', width: 750, height: 925 },
  { value: '8x10', label: '8" × 10" (Paperback)', width: 800, height: 1000 },
  { value: '8.25x6', label: '8.25" × 6" (Paperback)', width: 825, height: 600 },
  { value: '8.25x8.25', label: '8.25" × 8.25" (Paperback)', width: 825, height: 825 },
  { value: '8.5x8.5', label: '8.5" × 8.5" (Paperback)', width: 850, height: 850 },
  { value: '8.5x11', label: '8.5" × 11" (Paperback)', width: 850, height: 1100 },
  { value: '8.27x11.69', label: '8.27" × 11.69" (Paperback)', width: 827, height: 1169 },
  
  // Hardcover Trim Sizes
  { value: '5.5x8.5-hc', label: '5.5" × 8.5" (Hardcover)', width: 550, height: 850 },
  { value: '6x9-hc', label: '6" × 9" (Hardcover)', width: 600, height: 900 },
  { value: '6.14x9.21-hc', label: '6.14" × 9.21" (Hardcover)', width: 614, height: 921 },
  { value: '7x10-hc', label: '7" × 10" (Hardcover)', width: 700, height: 1000 },
  { value: '8.25x11-hc', label: '8.25" × 11" (Hardcover)', width: 825, height: 1100 }
];

const STORY_TYPES = [
  'Fairy Tale',
  'Adventure',
  'Mystery',
  'Fantasy',
  'Science Fiction',
  'Animal Story',
  'Educational',
  'Bedtime Story',
  'Custom'
];

const DRAWING_MEDIUMS = [
  'Digital Art',
  'Pencil',
  'Colored Pencil',
  'Pen',
  'Ink',
  'Marker',
  'Crayon',
  'Pastel',
  'Chalk',
  'Charcoal',
  'Watercolor',
  'Acrylic Paint',
  'Oil Paint',
  'Gouache',
  'Airbrush',
  'Spray Paint',
  'Mixed Media',
  'Custom'
];

const ART_STYLES = [
  'Children\'s Book Illustration',
  'Storybook Art',
  'Cartoon',
  'Anime/Manga',
  'Comic Book',
  'Studio Ghibli Style',
  'Disney Style',
  'Pixar Style',
  'Fantasy Art',
  'Realistic',
  'Semi-Realistic',
  'Stylized',
  'Sketch',
  'Line Art',
  'Pixel Art',
  'Vector Art',
  '3D Rendered',
  'Flat Design',
  'Minimalist',
  'Art Nouveau',
  'Art Deco',
  'Impressionist',
  'Abstract',
  'Pop Art',
  'Paper Cut',
  'Collage',
  'Vintage',
  'Retro',
  'Photorealistic',
  'Custom'
];

const STORY_TONES = [
  'Playful',
  'Whimsical',
  'Adventurous',
  'Mysterious',
  'Educational',
  'Calm',
  'Exciting',
  'Inspiring'
];

const StoryInfo: React.FC<StoryInfoProps> = ({
  storyType,
  drawingType,
  drawingStyle,
  storyTone,
  canvasSize,
  backgroundColor,
  customDrawingStyles = [],
  onUpdate,
  onOpenStyleModal,
  onOpenFontModal,
  onOpenDesignModal,
  showGrid = false,
  onToggleGrid,
  showPreviousPageOverlay = false,
  onTogglePreviousPageOverlay,
  previousPageOpacity = 0.3,
  onPreviousPageOpacityChange,
  hasPreviousPage = false
}) => {
  // Extract the art style from the combined drawingStyle string
  // Format: "Art Style, quality" -> extract just "Art Style"
  const extractArtStyle = (combinedStyle: string): string => {
    if (!combinedStyle) return 'Children\'s Book Illustration';
    
    // If it's already a simple style (no commas), return as is
    if (!combinedStyle.includes(',')) {
      return combinedStyle;
    }
    
    // Extract the first part (art style) from combined string
    const parts = combinedStyle.split(',').map(p => p.trim());
    const artStyle = parts[0];
    
    // Return the art style regardless of whether it's in the predefined list
    // This supports custom art styles entered by users
    return artStyle;
  };

  // When art style is changed, we need to reconstruct the combined drawingStyle
  const handleArtStyleChange = (newArtStyle: string) => {
    // For now, just set the art style directly
    // The AI prompt system will handle combining it properly
    onUpdate('drawingStyle', newArtStyle);
  };

  const displayArtStyle = extractArtStyle(drawingStyle);

  // Debug logging
  return (
    <div className="story-info">
      <div className="story-info-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Canvas Size:</span>
          <select
            value={canvasSize}
            onChange={(e) => onUpdate('canvasSize', e.target.value)}
            className="story-info-select"
          >
            {CANVAS_SIZES.map(size => (
              <option key={size.value} value={size.value}>{size.label}</option>
            ))}
          </select>

          {/* Background color selector placed next to Canvas Size */}
          <input
            type="color"
            title="Canvas background color"
            value={backgroundColor || '#ffffff'}
            onChange={(e) => onUpdate('backgroundColor', e.target.value)}
            style={{ width: 36, height: 28, padding: 0, border: 'none', background: 'transparent' }}
          />
          
          {/* Grid toggle button */}
          {onToggleGrid && (
            <button
              onClick={onToggleGrid}
              title={showGrid ? 'Hide guideline grid' : 'Show guideline grid'}
              style={{
                width: 36,
                height: 28,
                padding: 0,
                border: '1px solid #ccc',
                borderRadius: 4,
                background: showGrid ? '#3B82F6' : 'white',
                color: showGrid ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 4
              }}
            >
              ⊞
            </button>
          )}
          
          {/* Previous Page Overlay Toggle */}
          {onTogglePreviousPageOverlay && hasPreviousPage && (
            <button
              onClick={onTogglePreviousPageOverlay}
              title={showPreviousPageOverlay ? 'Hide previous page overlay' : 'Show previous page overlay'}
              style={{
                width: 36,
                height: 28,
                padding: 0,
                border: '1px solid #ccc',
                borderRadius: 4,
                background: showPreviousPageOverlay ? '#10B981' : 'white',
                color: showPreviousPageOverlay ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 4
              }}
            >
              ◫
            </button>
          )}
          
          {/* Opacity Slider for Previous Page Overlay */}
          {showPreviousPageOverlay && hasPreviousPage && onPreviousPageOpacityChange && (
            <input
              type="range"
              min="0.1"
              max="0.8"
              step="0.1"
              value={previousPageOpacity}
              onChange={(e) => onPreviousPageOpacityChange(parseFloat(e.target.value))}
              title={`Overlay opacity: ${Math.round(previousPageOpacity * 100)}%`}
              style={{
                width: 60,
                height: 28,
                marginLeft: 4,
                cursor: 'pointer'
              }}
            />
          )}
        </label>

        {/* Design Settings Button */}
        {onOpenDesignModal && (
          <button
            onClick={onOpenDesignModal}
            className="style-settings-btn"
            title="Manage page layout and design settings"
          >
            Design Settings
          </button>
        )}

        {/* Story Settings Button */}
        {onOpenStyleModal && (
          <button
            onClick={onOpenStyleModal}
            className="style-settings-btn"
            title="Manage story and visual style settings"
          >
            Story Settings
          </button>
        )}

        {/* Font Settings Button */}
        {onOpenFontModal && (
          <button
            onClick={onOpenFontModal}
            className="style-settings-btn"
            title="Set default font settings for new text elements"
          >
            Font Settings
          </button>
        )}
      </div>
    </div>
  );
};

export default StoryInfo;
